import {Box, Link, merge, type Text} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type React from 'react'
import {useEffect, useRef, type ReactElement, type ForwardedRef, type PropsWithChildren} from 'react'
import {testIdProps} from '@github-ui/test-id-props'
import {SafeHTMLText, type SafeHTMLString} from '@github-ui/safe-html'

import {useNestedListItemTitle} from './context/TitleContext'
import type {NestedListItemLeadingBadge} from './LeadingBadge'
import type {NestedListItemTrailingBadge} from './TrailingBadge'

export type NestedListItemTitleProps = PropsWithChildren<{
  /*
   * Styling for the div tag around the title, if children are included
   */
  containerSx?: BetterSystemStyleObject

  /*
   * An optional element used to indicate information such as the status of the item. Appears before the title text.
   */
  leadingBadge?: ReactElement<typeof NestedListItemLeadingBadge>

  /*
   * An optional element used to indicate information such as the status of the item. Appears after the title text.
   */
  trailingBadges?: Array<ReactElement<typeof NestedListItemTrailingBadge>>
}> &
  NestedListItemHeadingLinkProps

type NestedListItemHeadingLinkProps = {
  /*
   * Styling for the anchor tag of the title, if the title is a link
   */
  anchorSx?: BetterSystemStyleObject
  /**
   * A ref to the anchor element
   */
  anchorRef?: ForwardedRef<HTMLAnchorElement>
  /**
   * The link that will be opened when the item is clicked
   */
  href?: string
  /*
   * An optional prop to pass additional props for the anchor tag of the title, if the title is a link
   * Can be used to add a target or soft navigation, for example
   */
  additionalLinkProps?: React.ComponentPropsWithoutRef<typeof Text>
  /**
   * A click handler to be executed when the element is clicked
   */
  onClick?: React.MouseEventHandler<HTMLElement>
  /**
   * The tooltip to add context to the title
   */
  tooltip?: string
  /**
   * The text content of the header, to convey the primary meaning of the list item.
   */
  value: SafeHTMLString | string
}

const defaultAnchorStyles: BetterSystemStyleObject = {
  color: 'fg.default',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  '&:hover': {cursor: 'pointer', color: 'accent.fg', textDecoration: 'underline'},
}

const defaultContainerStyles: BetterSystemStyleObject = {
  gridArea: 'primary',
  flexGrow: 2,
  display: 'flex',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  pt: 2,
  pb: 2,
}

export function NestedListItemTitle({
  additionalLinkProps = {} as React.ComponentPropsWithoutRef<typeof Text>,
  anchorRef: forwardedAnchorRef,
  anchorSx: externalAnchorSx,
  children,
  containerSx: externalContainerSx,
  href,
  leadingBadge,
  onClick,
  tooltip,
  trailingBadges,
  value,
}: NestedListItemTitleProps) {
  const {setTitle} = useNestedListItemTitle()
  const fallbackAnchorRef = useRef<HTMLAnchorElement>(null)
  const anchorRef = forwardedAnchorRef || fallbackAnchorRef

  // Set title for NestedListItem aria-label property
  useEffect(() => setTitle(value), [setTitle, value])

  const containerSx = externalContainerSx
    ? merge<BetterSystemStyleObject>(defaultContainerStyles, externalContainerSx)
    : defaultContainerStyles

  const anchorSx = externalAnchorSx
    ? merge<BetterSystemStyleObject>(defaultAnchorStyles, externalAnchorSx)
    : defaultAnchorStyles

  return (
    <Box sx={containerSx} {...testIdProps('nested-listview-item-primary-container')}>
      {leadingBadge}
      <Link
        // Intentionally setting the role to presentation to prevent the link from being announced by screen readers
        // More information: https://ui.githubapp.com/storybook/?path=/docs/recipes-nestedlistview-documentation-accessibility-listitem--docs#titleprimary-action-example-markup
        role="presentation"
        ref={anchorRef}
        sx={anchorSx}
        // Use inline styles as these prefixes are not supported in sx
        style={{
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
        }}
        href={href}
        onClick={onClick}
        {...additionalLinkProps}
        {...testIdProps('listitem-title-link')}
      >
        <SafeHTMLText html={value as SafeHTMLString} title={tooltip} />
      </Link>
      {/* Can't use margin because the trailing badges need to wrap inline around the title
        so we add gap after text for spacing */}
      {trailingBadges && <Box as="span" sx={{pr: 1}} />}
      <Box as="span" sx={{textOverflow: 'ellipsis', overflow: 'hidden'}}>
        {trailingBadges}
      </Box>
      {children}
    </Box>
  )
}

NestedListItemTitle.displayName = 'NestedListItemTitle'
